import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  Skeleton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useSponsorCompanies } from '../../hooks/useSponsorCompanies';
import { SponsorCompany } from '../../services/sponsorService';

interface SponsorCarouselProps {
  onCompanyClick?: (company: SponsorCompany) => void;
}

const SponsorCompanyCard: React.FC<{
  company: SponsorCompany;
  onClick?: (company: SponsorCompany) => void;
}> = ({ company, onClick }) => {
  return (
    <Box
      sx={{
        mx: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick
          ? {
              transform: 'scale(1.05)',
              filter: 'brightness(1.1)',
            }
          : {},
      }}
      onClick={() => onClick?.(company)}
    >
      <Avatar
        src={company.logoUrl}
        alt={`${company.companyName}のロゴ`}
        sx={{
          width: 80,
          height: 80,
          bgcolor: 'white',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <BusinessIcon sx={{ fontSize: 40, color: 'grey.600' }} />
      </Avatar>
    </Box>
  );
};

const SponsorCarouselSkeleton: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', gap: 4, px: 2, justifyContent: 'center' }}>
      {[...Array(6)].map((_, index) => (
        <Skeleton
          key={index}
          variant="circular"
          width={80}
          height={80}
          sx={{ mx: 2 }}
        />
      ))}
    </Box>
  );
};

export const SponsorCarousel: React.FC<SponsorCarouselProps> = ({
  onCompanyClick,
}) => {
  const { companies, loading, error } = useSponsorCompanies();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 表示する企業数（画面サイズに応じて調整）
  const visibleCount = isMobile ? 3 : 6;
  const canScrollLeft = currentIndex > 0;
  const canScrollRight = currentIndex < companies.length - visibleCount;

  // 自動スクロール（オプション）
  useEffect(() => {
    if (companies.length <= visibleCount) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        return nextIndex >= companies.length - visibleCount + 1 ? 0 : nextIndex;
      });
    }, 5000); // 5秒ごとに自動スクロール

    return () => clearInterval(interval);
  }, [companies.length, visibleCount]);

  const handleScrollLeft = () => {
    if (canScrollLeft) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const handleScrollRight = () => {
    if (canScrollRight) {
      setCurrentIndex(
        Math.min(companies.length - visibleCount, currentIndex + 1)
      );
    }
  };

  // スクロール位置を更新
  useEffect(() => {
    if (scrollContainerRef.current) {
      const logoWidth = 112; // ロゴ幅 + マージン (80px + 32px)
      scrollContainerRef.current.scrollTo({
        left: currentIndex * logoWidth,
        behavior: 'smooth',
      });
    }
  }, [currentIndex]);

  // エラーまたは企業がない場合は何も表示しない
  if (error || (!loading && companies.length === 0)) {
    return null;
  }

  return (
    <Card
      sx={{
        mb: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* シンプルなヘッダー */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.8,
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            スポンサー企業
          </Typography>
        </Box>

        {/* カルーセル */}
        <Box sx={{ position: 'relative' }}>
          {/* 左スクロールボタン */}
          {!isMobile && companies.length > visibleCount && (
            <IconButton
              onClick={handleScrollLeft}
              disabled={!canScrollLeft}
              sx={{
                position: 'absolute',
                left: -16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
                '&:disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* スクロールコンテナ */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              overflowX: 'hidden',
              scrollBehavior: 'smooth',
              px: 2,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {loading ? (
              <SponsorCarouselSkeleton />
            ) : (
              companies.map((company) => (
                <SponsorCompanyCard
                  key={company.id}
                  company={company}
                  onClick={onCompanyClick}
                />
              ))
            )}
          </Box>

          {/* 右スクロールボタン */}
          {!isMobile && companies.length > visibleCount && (
            <IconButton
              onClick={handleScrollRight}
              disabled={!canScrollRight}
              sx={{
                position: 'absolute',
                right: -16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
                '&:disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>

        {/* インジケーター（モバイル用） */}
        {isMobile && companies.length > 1 && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}
          >
            {companies.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor:
                    index === currentIndex
                      ? 'white'
                      : 'rgba(255, 255, 255, 0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
